import { motion } from 'framer-motion'
import styled from 'styled-components'
import * as Accordion from '@radix-ui/react-accordion'
import { ArrowDownIcon } from '@velasca/icons/arrow-down'
import { CloseIcon } from '@velasca/icons/close'
import { Checkbox } from '@velasca/ui/checkbox'
import { UnstyledList } from '@velasca/ui/unstyled-list'

export const FooterButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 1.5rem;
`

export const MobileFilterButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem 2rem;
`

export const MobileControlsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export const MobileFilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  padding: 0.575rem 1.2rem;
`

export const Container = styled.div<{
  topDistance: number
  isSticky: boolean
}>`
  position: fixed;
  width: 100%;
  top: ${({ topDistance }) => `${topDistance}px`};
  display: flex;
  flex-direction: column;
  gap: 1rem;
  font-family: ${({ theme }) => theme.fonts.brand};
  z-index: 10;
  padding: 0 2rem;

  @media (min-width: 992px) {
    background-color: ${({ theme }) => theme.colors.background};
    position: relative;
    top: auto;
    gap: 0;
    margin: 0 auto 1.5rem auto;
    padding: 0;
    box-shadow: none;
  }
`

export const DesktopFilterButtonContainer = styled(motion.div)<{
  topDistance: number
}>`
  position: fixed;
  top: ${({ topDistance }) => `${topDistance}px`};
  left: 0;
  right: 0;
  font-family: ${({ theme }) => theme.fonts.brand};
  z-index: 10;
  max-width: var(--container-max-width);
  margin: 1.8rem auto;
  pointer-events: none;
  padding: 0.5rem 2.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export const FlexContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
  padding: 0 2rem;
  overflow-x: auto;
  scrollbar-width: none;
  flex-wrap: nowrap;

  @media (min-width: 992px) {
    gap: 1.5rem;
    max-width: var(--container-max-width);
    padding: 0 1.6rem;
    margin: 0 auto;
    justify-content: space-between;
    align-items: start;
  }

  &::-webkit-scrollbar {
    width: 0 !important;
  }

  > * {
    flex: none;
  }
`

export const FlexList = styled.div`
  display: flex;
  gap: 1rem;
  flex: 90%;
  align-items: center;
  flex-wrap: nowrap;
  overflow-x: scroll;

  @media (min-width: 992px) {
    flex-wrap: wrap;
    overflow-x: initial;
  }

  &::-webkit-scrollbar {
    width: 0 !important;
  }
`

export const ColorBackground = styled.div<{
  isSticky: boolean
}>`
  position: relative;
  padding: 1rem 0;
  width: 100%;

  @media (min-width: 992px) {
    padding: 1rem 0 1.5rem 0;
    margin: 0;
  }
`

export const RemoveRefinementBadge = styled.button`
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  background-color: #eeeeee;
  color: ${({ theme }) => theme.colors.text.primary};
  border: none;
  white-space: nowrap;

  line-height: 1.42857143;

  @media (min-width: 992px) {
    padding: 0.5rem 1.5rem;
  }
`

export const ActiveRefinementLabel = styled.div`
  display: flex;
  align-items: center;
  text-transform: uppercase;
  font-weight: 400;
  line-height: 125%;
  font-size: 1.4rem;
  white-space: nowrap;
`

export const ClearRefinementsButton = styled.button`
  display: none;
  @media (min-width: 992px) {
    display: flex;
    align-items: center;
    padding: 0;
    color: ${({ theme }) => theme.colors.danger};
    background-color: transparent;
    border: none;
    text-transform: uppercase;
  }
`

export const Close = styled.button`
  position: absolute;
  top: 2rem;
  right: 3rem;
  width: 2rem;
  height: 2rem;
  padding: 0;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.text.primary};
  z-index: 1000;
`

export const FacetValuesBackground = styled.div<{ isOpen: boolean }>`
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
  background-color: ${({ theme }) => theme.colors.background};

  @media (min-width: 992px) {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1;
    background-color: ${({ theme, isOpen }) =>
      isOpen ? theme.colors.background : theme.colors.boxBackground};
    box-shadow: 0px 20px 15px -15px rgba(0, 0, 0, 0.05);
  }
`

export const SkeletonContainer = styled.div`
  display: flex;
  height: 2.4rem;
  position: relative;
  align-items: center;
  gap: 1rem;
  margin: 0 0 2rem 0;

  @media (min-width: 992px) {
    margin: 0;
  }
`

export const StyledCheckbox = styled.div<{
  isChecked: boolean
  bulletColor?: string
}>`
  display: flex;
  height: 2.4rem;
  position: relative;
  align-items: center;

  &:not(:last-child) {
    margin: 0 0 1.7rem 0;
  }

  @media (min-width: 992px) {
    margin: 0 !important;
  }

  label,
  input {
    cursor: pointer;
  }

  input {
    width: 1.5rem;
    height: 1.5rem;
    padding: 0;
    opacity: 0;
    z-index: 20;
  }

  label {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin: 0;
    font-size: 1.5rem;
    font-weight: ${({ isChecked }) => (isChecked ? 400 : 300)};
    color: ${({ isChecked, theme }) =>
      isChecked ? theme.colors.primary : theme.colors.text.primary};
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    display: block;
    border-radius: 10rem;
  }

  &::before {
    left: 0;
    width: 1.5rem;
    height: 1.5rem;
    border-width: ${({ isChecked }) => (isChecked ? '0.25rem' : '0.125rem')};
    border-style: solid;
    border-color: ${({ isChecked, theme }) =>
      isChecked ? theme.colors.background : theme.colors.disabled};
    border-radius: 0;
    background-color: ${({ isChecked, bulletColor, theme }) =>
      isChecked && !bulletColor
        ? theme.colors.primary
        : (bulletColor ?? theme.colors.background)};
  }

  &::after {
    left: 0;
    width: 1.5rem;
    height: 1.5rem;

    border-width: 0.2rem;
    border-style: solid;
    border-radius: 0;
    border-color: ${({ theme }) => theme.colors.primary};
    opacity: ${({ isChecked }) => (isChecked ? 1 : 0)};
    transition: opacity 0.1618s;
  }
`

export const MainFilterContainer = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.boxBackground};
  font-family: ${({ theme }) => theme.fonts.brand};
  overflow-y: hidden;

  @media (min-width: 992px) {
    display: flex;
    justify-content: space-between;
    max-width: var(--container-max-width);
    padding: 0.5rem 1.5rem;
    margin: 0.1rem auto 0;
    overflow-y: auto;
  }
`

export const OpenFilterButton = styled.button<{ isCentered?: boolean }>`
  display: flex;
  position: relative;
  align-items: center;
  padding: 0.575rem 1.2rem;
  background: ${({ theme }) => theme.colors.boxBackground};
  border: none;
  font-size: 1.4rem;
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.text.primary};
  line-height: normal;
  margin: ${({ isCentered }) => (isCentered ? '0 auto' : '0')};
  pointer-events: auto;
  transition:
    color 0.1s,
    background-color 0.1s;

  &:active,
  &:hover {
    color: ${({ theme }) => theme.colors.background};
    background-color: ${({ theme }) => theme.colors.text.primary};
  }
`

export const FilterBy = styled.div`
  display: flex;
  align-items: center;
  padding: 2rem 0 2rem;
  background: transparent;
  font-size: 1.5rem;
  font-weight: 500;
  line-height: 1.5;
  text-transform: uppercase;
  padding-left: 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  @media (min-width: 992px) {
    padding: 0;
    margin-right: 1.5rem;
    border-bottom: none;
  }
`

export const PanelToggle = styled.button<{ isOpen: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  border: none;
  text-transform: uppercase;
  justify-content: center;
  padding: 0.75rem 2rem;
  background-color: ${({ isOpen, theme }) =>
    isOpen ? theme.colors.background : 'transparent'};
  border-radius: 0.5rem 0.5rem 0 0;
  color: ${({ isOpen, theme }) =>
    isOpen ? theme.colors.primary : theme.colors.text.primary};
  font-weight: 400;
  transition: background-color 0.2s;
`

export const LeftSide = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: 992px) {
    flex-direction: row;
    align-items: center;
  }
`

export const RightSide = styled.div`
  display: flex;
  align-items: center;
  font-size: 1.4rem;
`

export const Chevron = styled(ArrowDownIcon)<{ $rotate?: boolean }>`
  color: currentColor;
  width: 1.2rem;
  margin-left: 1rem;
  transform-origin: 50% 50%;
  transform: ${({ $rotate }) => ($rotate ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s;
`

export const FacetToggle = styled(Accordion.Trigger)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 0;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 1.4rem;
  font-weight: 400;
  text-transform: uppercase;

  &[data-state='open'] > ${Chevron} {
    transform: rotate(180deg);
  }

  &[data-state='open'] {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 500;
  }
`

export const StyledClosedIcon = styled(CloseIcon)`
  margin-right: 0.3rem;
  display: flex;
`

export const ScrollableArea = styled(UnstyledList)`
  overflow-y: scroll;
  padding: 0 1.5rem 1rem 1.5rem;

  /* 64px is the height of the FilterBy component */
  /* 106px is the height of the footer buttons */
  height: calc(100dvh - (64px + 106px));
  @supports not (height: 100dvh) {
    height: calc(100vh - (64px + 106px));
  }
`

export const AccordionItem = styled.li`
  border-bottom: 1px solid ${({ theme }) => theme.colors.secondary};
  padding: 0.6rem 0;
`

export const FacetsList = styled.div<{ isOpen: boolean }>`
  margin: 0.2rem;

  @media (min-width: 992px) {
    padding: 1.5rem 1.6rem 0.5rem 1.6rem;
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
    max-width: var(--container-max-width);
    margin: 0 auto;
    display: flex;
    flex-direction: column;
  }
`

export const FacetsContainer = styled.div`
  display: flex;
  flex-flow: column wrap;
  gap: 2rem;
  padding: 2rem 0;

  @media (min-width: 992px) {
    gap: 1.5rem;
    padding: 0 0 1.5rem 0;
  }
`

export const FacetsTitle = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.4rem;
  font-weight: 500;
  text-transform: uppercase;
`

export const FacetsGroup = styled.div`
  @media (min-width: 992px) {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 2rem;
  }

  // Questa regola fa in modo che il bordo appaia
  // solo se si trova a fianco dello stesso componente
  & + & {
    padding-top: 2rem;
    border-top: 0.1rem solid ${({ theme }) => theme.colors.border};

    @media (min-width: 992px) {
      padding-top: 0;
      border-top: none;
    }
  }
`

export const AccordionContent = styled(Accordion.Content)`
  overflow-y: hidden;

  &[data-state='open'] {
    animation: slideDown 0.8s ease-out;
  }
  &[data-state='closed'] {
    max-height: 0;
    animation: slideUp 0.2s ease-in;
  }

  @keyframes slideDown {
    from {
      max-height: 0;
    }
    to {
      /* 1000px is a arbitrary number, we just need to set a value that is bigger than the content. It's a trick to make the animation work. */
      max-height: 1500px;
    }
  }

  @keyframes slideUp {
    from {
      max-height: var(--radix-accordion-content-height);
    }
    to {
      max-height: 0;
    }
  }
`

export const RelativeContainer = styled.div`
  position: relative;
  background-color: ${({ theme }) => theme.colors.boxBackground};
`

export const NoResults = styled.div`
  font-size: 1.6rem;
  text-align: center;
  padding: 8px;
`

export const StyledFilterCheckbox = styled(Checkbox)`
  display: flex;
  align-items: center;
`

export const StyledLabel = styled.label`
  padding-left: 0.8rem;
`

export const NewFilterContainer = styled.div`
  cursor: pointer;
  padding: 1.5rem 0;

  label {
    cursor: pointer;
    margin: 0;
    text-transform: uppercase;
    font-weight: 500;
  }

  @media (min-width: 992px) {
    color: ${({ theme }) => theme.colors.primary};
    padding: 1.5rem 2rem;
  }
`

export const StickyFrontImageToggler = styled.div`
  pointer-events: auto;
  background-color: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.text.primary};
`
